import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getOrderByNumber,
  cancelOrderByNumber,
  submitReturnRequest,
} from '../services/api';
import './OrderDetail.css';

/* ═══ SVG Icons ═══ */
const base = (s, w) => ({
  width: s, height: s, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: w,
  strokeLinecap: 'round', strokeLinejoin: 'round',
});

const IcBack = ({ size = 18, sw = 2 }) => (
  <svg {...base(size, sw)}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
);

const IcCheck = ({ size = 18, sw = 2.2 }) => (
  <svg {...base(size, sw)}><polyline points="20 6 9 17 4 12" /></svg>
);

const IcClock = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IcBox = ({ size = 40, sw = 1.3 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3 20 7.5v9L12 21 4 16.5v-9Z" />
    <path d="M4 7.5 12 12l8-4.5M12 12v9" />
  </svg>
);

const IcAlert = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IcReturn = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M3.2 12a8.8 8.8 0 1 0 2.85-6.5" />
    <path d="M3 4.6v4.6h4.6" />
  </svg>
);

/* ─── Timeline steps ─── */
const TIMELINE = [
  { key: 'pending',   label: 'Order Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'shipped',   label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
];

const STATUS_ORDER = ['pending', 'confirmed', 'shipped', 'delivered'];

export default function OrderDetail() {
  const { orderNumber } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showReturnForm, setShowReturnForm] = useState(false);

  /* ─── Load order ─── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getOrderByNumber(orderNumber);
        if (!cancelled) setOrder(res.data);
      } catch (e) {
        if (!cancelled) setError('Unable to load order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber]);

  /* ─── Cancel ─── */
  const handleCancel = async () => {
    if (!window.confirm('Do you want to cancel this order?')) return;
    setActionLoading(true);
    try {
      await cancelOrderByNumber(orderNumber);
      const res = await getOrderByNumber(orderNumber);
      setOrder(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Cancellation failed.');
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── Submit return ─── */
  const handleReturnSubmit = async (data) => {
    setActionLoading(true);
    try {
      await submitReturnRequest(orderNumber, data);
      const res = await getOrderByNumber(orderNumber);
      setOrder(res.data);
      setShowReturnForm(false);
      alert('✅ Return request submitted! Admin will review soon.');
    } catch (e) {
      alert(e.response?.data?.error || 'Request failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (v) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v ?? 0);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="od-page">
        <div className="od-loading">
          <div className="od-spinner" />
          <p>Loading order…</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="od-page">
        <div className="od-container">
          <div className="od-error-box">
            <IcAlert size={32} />
            <p>{error || 'Order not found'}</p>
            <Link to="/orders" className="od-btn-primary">Back to Orders</Link>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = STATUS_ORDER.indexOf(order.status);
  const isCancelled = order.status === 'cancelled';
  const activeReturn = order.return_requests?.find(
    (r) => r.status === 'pending' || r.status === 'approved'
  );

  return (
    <div className="od-page">
      <div className="od-container">

        {/* ─── Back Link ─── */}
        <Link to="/orders" className="od-back">
          <IcBack size={16} /> Back to Orders
        </Link>

        {/* ─── Header ─── */}
        <div className="od-header">
          <div>
            <div className="od-order-num">#{order.order_number}</div>
            <div className="od-order-date">Placed on {formatDate(order.created_at)}</div>
          </div>
          <span className={`od-status od-status-${order.status}`}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
        </div>

        {/* ─── Timeline ─── */}
        {!isCancelled && (
          <div className="od-card">
            <h2 className="od-section-title">Order Timeline</h2>
            <div className="od-timeline">
              {TIMELINE.map((step, i) => {
                const isDone = i <= currentStep;
                const isActive = i === currentStep;
                return (
                  <div
                    key={step.key}
                    className={`od-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                  >
                    <div className="od-step-icon">
                      {isDone ? <IcCheck size={14} /> : <IcClock size={14} />}
                    </div>
                    <div className="od-step-label">{step.label}</div>
                    {i < TIMELINE.length - 1 && (
                      <div className="od-step-line" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Cancelled Notice ─── */}
        {isCancelled && (
          <div className="od-cancelled-notice">
            <IcAlert size={20} />
            <div>
              <strong>Order Cancelled</strong>
              <p>This order has been cancelled.</p>
            </div>
          </div>
        )}

        {/* ─── Active Return Notice ─── */}
        {activeReturn && (
          <div className="od-return-notice">
            <IcReturn size={20} />
            <div>
              <strong>
                {activeReturn.request_type === 'return' ? 'Return' : 'Exchange'} Request {activeReturn.status === 'pending' ? 'Pending' : 'Approved'}
              </strong>
              <p>Your request has been received. Our team will arrange pickup within 24-48 hours.</p>
              <p className="od-admin-note">
                Request ID: <strong>#{activeReturn.id}</strong> · Status: <strong>{activeReturn.status}</strong>
              </p>
              {activeReturn.admin_note && (
                <p className="od-admin-note">Message: {activeReturn.admin_note}</p>
              )}
            </div>
          </div>
        )}

        {/* ─── Items ─── */}
        <div className="od-card">
          <h2 className="od-section-title">Items</h2>
          <div className="od-items">
            {order.items.map((item) => (
              <div key={item.id} className="od-item">
                {item.image ? (
                  <img src={item.image} alt={item.product_name} className="od-item-img" />
                ) : (
                  <div className="od-item-img od-item-img-placeholder">
                    <IcBox size={24} />
                  </div>
                )}
                <div className="od-item-info">
                  <div className="od-item-name">{item.product_name}</div>
                  <div className="od-item-meta">
                    ₹{formatPrice(item.price)} × {item.quantity}
                  </div>
                </div>
                <div className="od-item-total">
                  ₹{formatPrice(item.price * item.quantity)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Price Breakdown ─── */}
        <div className="od-card">
          <h2 className="od-section-title">Price Breakdown</h2>
          <div className="od-price-list">
            <div className="od-price-row">
              <span>Subtotal</span>
              <span>₹{formatPrice(order.subtotal)}</span>
            </div>
            <div className="od-price-row">
              <span>Tax</span>
              <span>₹{formatPrice(order.tax_amount)}</span>
            </div>
            <div className="od-price-row">
              <span>Delivery</span>
              {Number(order.delivery_charge) === 0 ? (
                <span className="od-free">FREE</span>
              ) : (
                <span>₹{formatPrice(order.delivery_charge)}</span>
              )}
            </div>
            <div className="od-price-row">
              <span>Handling</span>
              <span>₹{formatPrice(order.handling_fee)}</span>
            </div>
            <div className="od-price-total">
              <span>Total</span>
              <span>₹{formatPrice(order.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* ─── Shipping Address ─── */}
        <div className="od-card">
          <h2 className="od-section-title">Shipping Address</h2>
          <div className="od-address">
            <strong>{order.full_name}</strong>
            <div>{order.phone}</div>
            <div>{order.address}</div>
            <div>{order.city} - {order.pincode}</div>
          </div>
        </div>

        {/* ─── Return Policy Warning (only if return available) ─── */}
        {order.can_return && !activeReturn && (
          <div className="od-return-warning">
            <div className="od-warning-icon">
              <IcAlert size={20} />
            </div>
            <div className="od-warning-content">
              <h4 className="od-warning-title">Return Policy Notice</h4>
              <ul className="od-warning-list">
                <li>Product must be in <strong>original condition</strong></li>
                <li>Tags, packaging, and invoice must be <strong>included</strong></li>
                <li><strong>No refund</strong> for used, damaged, or fake products</li>
                <li>Refund will be processed only after <strong>quality check</strong></li>
                <li>Return window is <strong>7 days</strong> from delivery date</li>
              </ul>
            </div>
          </div>
        )}

        {/* ─── Actions ─── */}
        <div className="od-actions">
          {order.can_cancel && (
            <button
              className="od-btn od-btn-cancel"
              onClick={handleCancel}
              disabled={actionLoading}
            >
              {actionLoading ? 'Cancelling…' : 'Cancel Order'}
            </button>
          )}
          {order.can_return && !activeReturn && (
            <button
              className="od-btn od-btn-return"
              onClick={() => setShowReturnForm(true)}
              disabled={actionLoading}
            >
              <IcReturn size={16} /> Request Return / Exchange
            </button>
          )}
        </div>
      </div>

      {/* ─── Return Modal ─── */}
      {showReturnForm && (
        <ReturnForm
          onClose={() => setShowReturnForm(false)}
          onSubmit={handleReturnSubmit}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   Return Form Modal
═══════════════════════════════════════════════════════ */
function ReturnForm({ onClose, onSubmit, loading }) {
  const [form, setForm] = useState({
    request_type: 'return',
    reason: 'size',
    reason_text: '',
    refund_method: 'original',
    refund_detail: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="od-modal-overlay" onClick={onClose}>
      <div className="od-modal" onClick={(e) => e.stopPropagation()}>
        <div className="od-modal-header">
          <h3>Return / Exchange Request</h3>
          <button className="od-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="od-modal-body">
          {/* Type */}
          <div className="od-form-group">
            <label>Type</label>
            <div className="od-radio-row">
              <label className={`od-radio ${form.request_type === 'return' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="return"
                  checked={form.request_type === 'return'}
                  onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                />
                Return
              </label>
              <label className={`od-radio ${form.request_type === 'exchange' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="exchange"
                  checked={form.request_type === 'exchange'}
                  onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                />
                Exchange
              </label>
            </div>
          </div>

          {/* Reason */}
          <div className="od-form-group">
            <label>Reason</label>
            <select
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="od-select"
            >
              <option value="size">Size issue</option>
              <option value="damaged">Damaged product</option>
              <option value="wrong">Wrong item received</option>
              <option value="quality">Not as described</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Details */}
          <div className="od-form-group">
            <label>Additional details (optional)</label>
            <textarea
              value={form.reason_text}
              onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
              rows="3"
              placeholder="Explain briefly..."
              className="od-textarea"
            />
          </div>

          {/* Refund method (only for return) */}
          {form.request_type === 'return' && (
            <>
              <div className="od-form-group">
                <label>Refund Method</label>
                <select
                  value={form.refund_method}
                  onChange={(e) => setForm({ ...form, refund_method: e.target.value })}
                  className="od-select"
                >
                  <option value="original">Original payment method</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank transfer</option>
                </select>
              </div>

              {form.refund_method !== 'original' && (
                <div className="od-form-group">
                  <label>
                    {form.refund_method === 'upi' ? 'UPI ID' : 'Bank Account Details'}
                  </label>
                  <input
                    type="text"
                    value={form.refund_detail}
                    onChange={(e) => setForm({ ...form, refund_detail: e.target.value })}
                    placeholder={form.refund_method === 'upi' ? 'yourname@upi' : 'Account no, IFSC'}
                    className="od-input"
                    required
                  />
                </div>
              )}
            </>
          )}

          <button type="submit" className="od-btn od-btn-return" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
}